import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AiAssistantApiError,
  fetchAiProjectsPrefetch,
  sendAiMessage,
} from "../api/aiAssistantApi";
import useAiCitySlug from "../hooks/useAiCitySlug";
import useAiRouteFilters from "../hooks/useAiRouteFilters";
import { useThankYouModal } from "./ThankYouModalContext";
import {
  buildPrefetchListingType,
  clearStoredAiSessionId,
  createDefaultAiContext,
  createEphemeralAiSessionId,
  createMessageId,
  createNewAiSessionId,
  AI_LEAD_SOURCE,
  mergeContext,
  normalizeAssistantReply,
  parseLeadFormMessage,
} from "../utils/aiAssistantTypes";

const AiAssistantContext = createContext(null);

export function AiAssistantProvider({ children }) {
  const citySlug = useAiCitySlug();
  const routeFilters = useAiRouteFilters();
  const { openThankYouModal } = useThankYouModal();
  const sessionIdRef = useRef(createEphemeralAiSessionId());
  const openerSentRef = useRef(false);
  const welcomeLockRef = useRef(false);
  const sendWelcomeRef = useRef(null);
  const applyResponseRef = useRef(null);
  const prefetchProjectsRef = useRef(null);
  const contextRef = useRef(createDefaultAiContext(citySlug, routeFilters));
  const lastUserMessageRef = useRef("");
  const requestGenerationRef = useRef(0);
  const messagesRef = useRef([]);
  const prefetchDoneRef = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [context, setContext] = useState(() => createDefaultAiContext(citySlug, routeFilters));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [agentMode, setAgentMode] = useState(false);

  const mergeRouteIntoContext = useCallback(
    (base) => {
      const next = createDefaultAiContext(citySlug, routeFilters);
      const apiFilters = base?.filters && typeof base.filters === "object" ? base.filters : {};
      return {
        ...base,
        step: base?.step || next.step,
        filters: {
          ...next.filters,
          ...apiFilters,
          city: citySlug,
          ...(apiFilters.property_type == null && routeFilters.property_type
            ? { property_type: routeFilters.property_type }
            : {}),
          ...(apiFilters.listing_type == null && routeFilters.listing_type
            ? { listing_type: routeFilters.listing_type }
            : {}),
        },
      };
    },
    [citySlug, routeFilters]
  );

  useEffect(() => {
    clearStoredAiSessionId();
  }, []);

  useEffect(() => {
    const merged = mergeRouteIntoContext(contextRef.current);
    contextRef.current = merged;
    setContext(merged);
  }, [mergeRouteIntoContext]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const lastResponse = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === "assistant" && messages[i]?.response) {
        return messages[i].response;
      }
    }
    return null;
  }, [messages]);

  const clearError = useCallback(() => setError(null), []);

  const openAssistant = useCallback(() => {
    setIsOpen(true);
    setLeadSuccess(false);
  }, []);

  const closeAssistant = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleAssistant = useCallback(() => {
    setIsOpen((prev) => !prev);
    setLeadSuccess(false);
  }, []);

  const resetAfterLead = useCallback(
    (response) => {
      const formData = response?.form_data || contextRef.current?.form_data || {};
      const listingType =
        contextRef.current?.filters?.property_type || routeFilters.property_type || "apartment";
      const isRentListing = contextRef.current?.filters?.listing_type === "rent";
      const searchLocation = contextRef.current?.filters?.location || formData.location;

      const fresh = mergeRouteIntoContext(createDefaultAiContext(citySlug, routeFilters));
      contextRef.current = fresh;
      setContext(fresh);
      setLeadSuccess(true);
      window.setTimeout(() => setLeadSuccess(false), 5000);

      openThankYouModal({
        leadData: { ...formData, source: AI_LEAD_SOURCE, sub_source: AI_LEAD_SOURCE },
        listingType,
        isRentListing,
        searchLocation,
        displayTitle: formData.project_name || formData.project || undefined,
      });
    },
    [citySlug, routeFilters, mergeRouteIntoContext, openThankYouModal]
  );

  const applyResponse = useCallback(
    (response) => {
      const nextContext = mergeContext(contextRef.current, response, citySlug);
      const merged = mergeRouteIntoContext(nextContext);
      contextRef.current = merged;
      setContext(merged);
      setAgentMode(Boolean(response?.agent_mode));

      if (response?.lead_submitted) {
        resetAfterLead(response);
      }
    },
    [citySlug, mergeRouteIntoContext, resetAfterLead]
  );

  const prefetchProjects = useCallback(async () => {
    if (prefetchDoneRef.current) return;
    prefetchDoneRef.current = true;
    try {
      const type = buildPrefetchListingType(routeFilters);
      await fetchAiProjectsPrefetch(type, citySlug);
    } catch {
      prefetchDoneRef.current = false;
    }
  }, [citySlug, routeFilters]);

  const postToAssistant = useCallback(
    async (trimmed) => {
      const generation = requestGenerationRef.current;
      setIsLoading(true);
      setError(null);
      setLeadSuccess(false);

      try {
        const response = await sendAiMessage(
          trimmed,
          sessionIdRef.current,
          contextRef.current
        );

        if (generation !== requestGenerationRef.current) return;

        const assistantText = normalizeAssistantReply(response?.reply);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && last.text === assistantText) {
            return prev;
          }
          return [
            ...prev,
            {
              id: createMessageId(),
              role: "assistant",
              text: assistantText,
              response,
            },
          ];
        });

        if (response?.session_id) {
          sessionIdRef.current = response.session_id;
        }

        applyResponse(response);
      } catch (err) {
        if (generation !== requestGenerationRef.current) return;
        const message =
          err instanceof AiAssistantApiError
            ? err.message
            : err?.message || "Something went wrong. Please try again.";
        setError(message);
      } finally {
        if (generation === requestGenerationRef.current) {
          setIsLoading(false);
        }
      }
    },
    [applyResponse]
  );

  const sendUserMessage = useCallback(
    async (text, { silent = false } = {}) => {
      const trimmed = String(text ?? "").trim();
      if (!trimmed || isLoading) return;

      lastUserMessageRef.current = trimmed;

      if (contextRef.current?.step === "form") {
        const parsed = parseLeadFormMessage(trimmed);
        contextRef.current = {
          ...contextRef.current,
          form_data: {
            ...(contextRef.current.form_data || {}),
            ...parsed,
            sub_source: AI_LEAD_SOURCE,
            source: AI_LEAD_SOURCE,
          },
        };
      }

      if (!silent) {
        setMessages((prev) => [
          ...prev,
          { id: createMessageId(), role: "user", text: trimmed },
        ]);
      }

      await postToAssistant(trimmed);
    },
    [isLoading, postToAssistant]
  );

  const retryLastMessage = useCallback(async () => {
    const trimmed = lastUserMessageRef.current;
    if (!trimmed || isLoading) return;
    clearError();
    await postToAssistant(trimmed);
  }, [isLoading, postToAssistant, clearError]);

  const sendWelcome = useCallback(async () => {
    if (welcomeLockRef.current || openerSentRef.current) return;
    welcomeLockRef.current = true;
    openerSentRef.current = true;

    const generation = requestGenerationRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendAiMessage(
        "Hi",
        sessionIdRef.current,
        contextRef.current
      );

      if (generation !== requestGenerationRef.current) return;

      const assistantText = normalizeAssistantReply(response?.reply);
      setMessages((prev) => {
        if (prev.some((msg) => msg.role === "assistant")) return prev;
        return [
          {
            id: createMessageId(),
            role: "assistant",
            text: assistantText,
            response,
          },
        ];
      });

      if (response?.session_id) {
        sessionIdRef.current = response.session_id;
      }

      applyResponseRef.current?.(response);
      prefetchProjectsRef.current?.();
    } catch (err) {
      if (generation !== requestGenerationRef.current) return;
      openerSentRef.current = false;
      const message =
        err instanceof AiAssistantApiError
          ? err.message
          : err?.message || "Something went wrong. Please try again.";
      setError(message);
    } finally {
      welcomeLockRef.current = false;
      if (generation === requestGenerationRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  sendWelcomeRef.current = sendWelcome;
  applyResponseRef.current = applyResponse;
  prefetchProjectsRef.current = prefetchProjects;

  const clearChat = useCallback(async () => {
    requestGenerationRef.current += 1;

    sessionIdRef.current = createNewAiSessionId();
    openerSentRef.current = false;
    welcomeLockRef.current = false;
    prefetchDoneRef.current = false;
    lastUserMessageRef.current = "";

    setMessages([]);
    setError(null);
    setLeadSuccess(false);
    setAgentMode(false);
    setIsLoading(false);

    const fresh = mergeRouteIntoContext(createDefaultAiContext(citySlug, routeFilters));
    contextRef.current = fresh;
    setContext(fresh);

    await sendWelcomeRef.current?.();
  }, [citySlug, routeFilters, mergeRouteIntoContext]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (messagesRef.current.length > 0 || openerSentRef.current || welcomeLockRef.current) {
      return undefined;
    }

    sendWelcomeRef.current?.();

    return undefined;
  }, [isOpen]);

  const value = useMemo(
    () => ({
      isOpen,
      openAssistant,
      closeAssistant,
      toggleAssistant,
      messages,
      context,
      lastResponse,
      isLoading,
      error,
      clearError,
      sendUserMessage,
      retryLastMessage,
      clearChat,
      leadSuccess,
      agentMode,
      citySlug,
      sessionId: sessionIdRef.current,
    }),
    [
      isOpen,
      openAssistant,
      closeAssistant,
      toggleAssistant,
      messages,
      context,
      lastResponse,
      isLoading,
      error,
      clearError,
      sendUserMessage,
      retryLastMessage,
      clearChat,
      leadSuccess,
      agentMode,
      citySlug,
    ]
  );

  return (
    <AiAssistantContext.Provider value={value}>{children}</AiAssistantContext.Provider>
  );
}

export function useAiAssistant() {
  const ctx = useContext(AiAssistantContext);
  if (!ctx) {
    throw new Error("useAiAssistant must be used within AiAssistantProvider");
  }
  return ctx;
}

export default AiAssistantContext;
